import React, { useMemo } from 'react';
import { Typography } from 'antd';
import { Column } from '@ant-design/plots';
import { buildLineChartData, LineMetricSeries } from './lineChartData';

const { Text } = Typography;

interface Props {
  title?: string;
  data?: LineMetricSeries[];
  fields?: string[];
  metricLabels?: Record<string, string>;
  metrics?: any[];  // All metrics for ID lookup
  areaNames?: Record<number, string>;
  showParent?: boolean;
  showChildren?: boolean;
  previewFallback?: boolean;
  fallbackMetricKey?: string;
  previewAreaId?: number;  // Area context for metric links
  onAreaClick?: (areaId: number) => void;
  onMetricClick?: (metricKey: string, metricId: number | undefined, areaId: number) => void;
}

const DEFAULT_PALETTE = ['#1d4ed8', '#f97316', '#16a34a', '#f59e0b', '#ef4444', '#7c3aed'];

const toRgba = (hexColor: string, alpha: number) => {
  const hex = hexColor.replace('#', '').trim();
  if (hex.length === 3) {
    const r = parseInt(hex[0] + hex[0], 16);
    const g = parseInt(hex[1] + hex[1], 16);
    const b = parseInt(hex[2] + hex[2], 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }
  if (hex.length === 6) {
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }
  return `rgba(22, 119, 255, ${alpha})`;
};

export const ReportBarChart: React.FC<Props> = ({
  title,
  data,
  fields,
  metricLabels,
  metrics,
  areaNames,
  showParent = true,
  showChildren = false,
  previewFallback,
  fallbackMetricKey,
  previewAreaId,
}) => {
  const { accentColor, palette } = useMemo(() => {
    const lineData = Array.isArray(data) ? data : [];
    const primaryMetricKey = lineData[0]?.metricKey || fallbackMetricKey;
    const metric = Array.isArray(metrics)
      ? metrics.find((entry) => entry?.key === primaryMetricKey)
      : undefined;
    const metricPalette = Array.isArray(metric?.colorPalette) ? metric.colorPalette.filter(Boolean) : [];
    const resolvedPalette = metricPalette.length > 0 ? metricPalette : DEFAULT_PALETTE;
    return {
      accentColor: resolvedPalette[0],
      palette: resolvedPalette,
    };
  }, [data, metrics, fallbackMetricKey]);

  const { displayPoints, unitLabel } = useMemo(
    () =>
      buildLineChartData({
        data,
        fields,
        metricLabels,
        areaNames,
        showParent,
        showChildren,
        previewFallback,
        fallbackMetricKey,
      }),
    [data, fields, metricLabels, areaNames, showParent, showChildren, previewFallback, fallbackMetricKey]
  );

  const formatX = (value: Date | number | string) => {
    if (value instanceof Date) {
      return value.toISOString().split('T')[0];
    }
    return String(value);
  };

  const chartData = displayPoints.map(({ __seriesKey, __sortKey, __isParent, ...rest }) => ({
    ...rest,
    x: formatX(rest.x),
  }));

  const seriesColors = useMemo(() => {
    const map = new Map<string, string>();
    let colorIndex = 0;
    chartData.forEach((row) => {
      const series = String((row as any).series);
      if (!map.has(series)) {
        map.set(series, palette[colorIndex % palette.length]);
        colorIndex += 1;
      }
    });
    return map;
  }, [chartData, palette]);

  const formatNumericValue = (raw: unknown) => {
    const num = typeof raw === 'number' ? raw : Number(raw);
    if (!Number.isFinite(num)) return null;
    const formatted = num.toFixed(2);
    return unitLabel ? `${formatted} ${unitLabel}` : formatted;
  };

  const config: any = {
    data: chartData,
    xField: 'x',
    yField: 'value',
    seriesField: 'series',
    isGroup: true,
    colorField: 'series',
    color: palette,
    legend: { color: { position: 'top', title: 'Aggregation' } },
    scale: {
      x: { type: 'band' },
      y: { type: 'linear' },
    },
    height: 236,
    tooltip: {
      shared: true,
      items: [
        (datum: any) => {
          const series = String(datum?.series ?? '');
          const formatted = formatNumericValue(datum?.value);
          return {
            name: series || 'Value',
            value: formatted ?? '—',
            color: seriesColors.get(series) || palette[0],
          };
        },
      ],
    },
    axis: {
      y: { title: unitLabel || false },
      x: {
        title: false,
        label: {
          autoHide: false,
          autoRotate: true,
        },
      },
    },
    animation: false,
  };

  const areaLabel =
    previewAreaId && areaNames?.[previewAreaId]
      ? areaNames[previewAreaId]
      : undefined;

  return displayPoints.length > 0 ? (
    <div
      className="report-bar-chart-surface"
      style={
        {
          '--report-chart-accent': accentColor,
          '--report-chart-accent-soft': toRgba(accentColor, 0.1),
        } as React.CSSProperties
      }
    >
      {title || areaLabel ? (
        <div className="report-bar-chart-head">
          <div className="report-bar-chart-title">{title || 'Bar chart'}</div>
          {areaLabel ? <div className="report-bar-chart-area">{areaLabel}</div> : null}
        </div>
      ) : null}
      {unitLabel ? (
        <div className="report-bar-chart-meta">
          <span className="report-bar-chart-unit">{unitLabel}</span>
        </div>
      ) : null}
      <Column {...config} />
    </div>
  ) : (
    <Text type="secondary">No data</Text>
  );
};

export default ReportBarChart;
